import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { MessageSquare, AlertCircle } from "lucide-react";

const MessageNotes = ({
  formData,
  setFormData,
  metadata,
  setMetadata,
  isColorful = false,
}) => {
  const labelCls = "text-xs font-medium text-muted-foreground";
  return (
    <div
      className={cn(
        " mt-4 rounded-lg border border-l-4 overflow-hidden w-full",
        isColorful
          ? "border-purple-200/60 dark:border-purple-700/40 border-l-purple-500"
          : "border-border border-l-purple-500",
      )}
    >
      {/* Section Header */}
      <div
        className={cn(
          "flex items-center gap-3 px-4 py-3 border-b",
          isColorful
            ? "bg-gradient-to-r from-slate-50/80 to-purple-50/50 dark:from-slate-800 dark:to-purple-900/20 border-purple-200/60 dark:border-purple-700/40"
            : "bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border-border",
        )}
      >
        <div
          className={cn(
            "p-2 rounded-lg shadow-sm",
            isColorful
              ? "colorful-section-invoice-message colorful-icon-hover"
              : "bg-gradient-to-br from-purple-500 to-purple-700",
          )}
        >
          <MessageSquare className="h-4 w-4 text-white" />
        </div>
        <span
          className={cn(
            "text-sm font-semibold uppercase tracking-wide",
            isColorful
              ? "bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 bg-clip-text text-transparent font-bold"
              : "text-foreground",
          )}
        >
          Message & Notes
        </span>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-1">
            <label className={labelCls}>Message (Max 200 characters)</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => {
                if (e.target.value.length <= 200)
                  setFormData({ ...formData, notes: e.target.value });
              }}
              rows={3}
              className="text-xs"
              maxLength={200}
              placeholder="Message to display on invoice..."
            />
            <p className="text-[10px] text-muted-foreground text-right">
              {formData.notes.length}/200
            </p>
          </div>
          <div className="space-y-1">
            <label className={labelCls}>Memo for internal use</label>
            <Textarea
              value={formData.internal_notes}
              onChange={(e) =>
                setFormData({ ...formData, internal_notes: e.target.value })
              }
              rows={3}
              className="text-xs"
              placeholder="Internal memo..."
            />
          </div>
        </div>

        {/* Payment Display Options */}
        <div className="flex items-center gap-6 mt-4">
          {[
            {
              id: "show_wire",
              label: "Show Wire Transfer",
              key: "show_wire_transfer",
            },
            {
              id: "show_zelle",
              label: "Show Zelle Details",
              key: "show_zelle_details",
            },
            {
              id: "show_cc",
              label: "Show Credit Card Details",
              key: "show_credit_card_details",
            },
          ].map((opt) => (
            <div
              key={opt?.id}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-lg",
                isColorful
                  ? "bg-purple-50/50 dark:bg-purple-900/10"
                  : "bg-muted/30",
              )}
            >
              <Checkbox
                id={opt.id}
                checked={metadata[opt?.key]}
                onChange={(checked) =>
                  setMetadata({ ...metadata, [opt?.key || ""]: !!checked })
                }
                className="h-3.5 w-3.5"
              />
              <label htmlFor={opt.id} className="text-xs cursor-pointer">
                {opt.label}
              </label>
            </div>
          ))}
        </div>

        {/* Payment Status */}
        <div
          className={cn(
            "flex items-center gap-2 mt-3 text-xs px-3 py-2 rounded-lg w-fit",
            "bg-destructive/10 text-destructive",
          )}
        >
          <AlertCircle className="h-3.5 w-3.5" />
          <span>Payment has not been received</span>
        </div>
      </div>
    </div>
  );
};

export default MessageNotes;

const Checkbox = ({ label, checked, onChange }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/30">
    <input
      type="checkbox"
      checked={checked || false}
      onChange={(e) => onChange(e.target.checked)}
      className="h-3.5 w-3.5"
    />
    <label className="text-xs font-medium cursor-pointer">{label}</label>
  </div>
);
